import type { RefObject, VoidFunctionComponent } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import OpenIssueInFullPage from './OpenIssueInFullPage';
import IssueDetailsModalsProvider, { useIssueDetailsModals } from '../IssueDetailsModalsContext';
import Drawer from '../../../generic/Drawer';
import * as IssuesV2Store from '../../../../redux/issuesV2';
import useIssueDetailsTabs from '../Tabs/useIssueDetailsTabs';
import VulnerabilityIssueContent from '../IssueContent/VulnerabilityIssueContent';
import { Category } from '../../../../../shared/issuesV2';
import LicensingIssueContent from '../IssueContent/LicensingIssueContent';
import QualityIssueContent from '../IssueContent/QualityIssueContent';
import { ISSUE_DRAWER_SLIDE_DURATION } from './constants';
import ShareLink from './ShareLink';
import Errors from '../Errors';

type Props = {
  clickTarget: RefObject<HTMLDivElement>;
  category: Category;
};

/**
 * IssueDetailsDrawer displays the issue details in a handy Drawer.
 */
const IssueDetailsDrawer: VoidFunctionComponent<Props> = ({ clickTarget, category }) => {
  const dispatch = useDispatch();
  const isOpen = useSelector(IssuesV2Store.selectors.getIsIssueDetailsDrawerOpen);
  const { errors } = useSelector(IssuesV2Store.selectors.getAsyncStatusIssueDetails);
  const [activeTab, setActiveTab] = useIssueDetailsTabs(isOpen);

  const { isAnyModalOpen } = useIssueDetailsModals();
  const onClose = () => dispatch(IssuesV2Store.actions.closeDrawer());
  const hasErrors = !!errors?.length;

  return (
    <Drawer
      isOpen={isOpen}
      clickTarget={clickTarget}
      onClose={onClose}
      shouldSkipClickOutside={isAnyModalOpen}
      buttonsSlot={
        !hasErrors && (
          <>
            <OpenIssueInFullPage />
            <ShareLink />
          </>
        )
      }
      slideDuration={ISSUE_DRAWER_SLIDE_DURATION}
    >
      {hasErrors ? (
        <Errors errors={errors} />
      ) : (
        <>
          {category === Category.Vulnerability && (
            <VulnerabilityIssueContent activeTab={activeTab} setActiveTab={setActiveTab} />
          )}
          {category === Category.Licensing && (
            <LicensingIssueContent activeTab={activeTab} setActiveTab={setActiveTab} />
          )}
          {category === Category.Quality && (
            <QualityIssueContent activeTab={activeTab} setActiveTab={setActiveTab} />
          )}
        </>
      )}
    </Drawer>
  );
};

/**
 * Wrapper which provides the IssueDetailsModalsProvider context to the IssueDetailsDrawer.
 * This is needed because we read from the modals context in the top-level component.
 */
const IssueDetailsDrawerContainer: VoidFunctionComponent<Props> = (props) => (
  <IssueDetailsModalsProvider>
    <IssueDetailsDrawer
      // eslint-disable-next-line react/jsx-props-no-spreading
      {...props}
    />
  </IssueDetailsModalsProvider>
);

export default IssueDetailsDrawerContainer;
